/**
 * 2048 PK 战斗特效与粒子物理动效系统 (Battle Effects & Canvas Confetti)
 * 职责：
 * 1. 真实物理抛物线弹道攻击抛射 (Parabolic Ballistic Trajectory)
 * 2. 聚能光球、彗星尾光粒子流与冲击波爆破光环
 * 3. 棋盘受击震颤与绝境破碎
 * 4. 漂浮表情弹幕与胜利礼花
 */

import confetti from "canvas-confetti";

export interface ProjectileOptions {
  /** true = 我方发射 (青蓝色左/上航道)，false = 敌方来袭 (猩红色右/下航道) */
  isOutbound: boolean;
  /** true = 友方空投救援包 (翡翠绿救援航道) */
  isRescue?: boolean;
  /** 弹体内部携带的直观载荷文字，如 "÷2" 或 "+2" */
  label: string;
  /** 命中瞬间在目标盘面弹出的简短战果浮字，如 "512 ➔ 256" 或 "+2 塞块" */
  impactText?: string;
}

export class BattleEffects {
  private overlayEl: HTMLElement;

  constructor(overlayEl: HTMLElement) {
    this.overlayEl = overlayEl;
  }

  /**
   * 双航道物理抛物线弹道系统 (Dual-Lane Parabolic Ballistic System)
   * 特点：
   * 1. 攻防异轨：我方进攻固定走左侧/上方弧线，敌方来袭固定走右侧/下方弧线，绝不重叠
   * 2. 阵营分色：我方电光青蓝 (#38bdf8)，敌方警戒猩红 (#f43f5e)
   * 3. 载荷可视：光球内直接显示 "÷2" 或 "+2"，落地瞬间同步引爆棋盘特效与浮动战果
   */
  public launchBallisticProjectile(
    fromEl: HTMLElement,
    toEl: HTMLElement,
    options: ProjectileOptions,
    onImpact?: () => void
  ) {
    if (!fromEl || !toEl || !this.overlayEl) {
      onImpact?.();
      return;
    }

    const overlayRect = this.overlayEl.getBoundingClientRect();
    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();

    const p0 = {
      x: fromRect.left + fromRect.width / 2 - overlayRect.left,
      y: fromRect.top + fromRect.height / 2 - overlayRect.top,
    };
    const p1 = {
      x: toRect.left + toRect.width / 2 - overlayRect.left,
      y: toRect.top + toRect.height / 2 - overlayRect.top,
    };

    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const dist = Math.hypot(dx, dy);
    const laneOffset = Math.min(115, Math.max(68, dist * 0.28));

    // 区分移动端竖屏 (上下布局) 与桌面端横屏 (左右布局)，构建永不重叠且绝不飞出屏幕的双航道控制点
    let midX: number;
    let midY: number;
    if (Math.abs(dy) >= Math.abs(dx)) {
      // 竖屏上下布局：我方走左弧线 (-laneOffset)，敌方走右弧线 (+laneOffset)
      midX = (p0.x + p1.x) / 2 + (options.isOutbound ? -laneOffset : laneOffset);
      midY = (p0.y + p1.y) / 2;
    } else {
      // 横屏左右布局：我方走上弧线 (-laneOffset)，敌方走下弧线 (+laneOffset)
      midX = (p0.x + p1.x) / 2;
      midY = (p0.y + p1.y) / 2 + (options.isOutbound ? -laneOffset : laneOffset);
    }

    const factionClass: "outbound" | "inbound" | "rescue" = options.isRescue
      ? "rescue"
      : options.isOutbound
        ? "outbound"
        : "inbound";

    // 1. 在目标中心创建锁定准星光环，预示落点
    const reticle = document.createElement("div");
    reticle.className = `target-reticle reticle-${factionClass}`;
    reticle.style.left = `${p1.x}px`;
    reticle.style.top = `${p1.y}px`;
    this.overlayEl.appendChild(reticle);

    // 2. 创建携带直观文字载荷 ("÷2" / "+2") 的抛射光球
    const orb = document.createElement("div");
    orb.className = `ballistic-orb orb-${factionClass}`;
    const core = document.createElement("div");
    core.className = "orb-core";
    const labelSpan = document.createElement("span");
    labelSpan.className = "orb-label";
    labelSpan.textContent = options.label;
    core.appendChild(labelSpan);
    orb.appendChild(core);

    this.overlayEl.appendChild(orb);

    const duration = 520;
    const startTime = performance.now();
    let lastSparkTime = 0;

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);

      // 二次贝塞尔曲线物理插值
      const invT = 1 - t;
      const curX = invT * invT * p0.x + 2 * invT * t * midX + t * t * p1.x;
      const curY = invT * invT * p0.y + 2 * invT * t * midY + t * t * p1.y;

      // 飞行高度透视缩放 (高空放大，两端收束)
      const scale = 0.9 + 0.42 * Math.sin(t * Math.PI);
      orb.style.transform = `translate3d(${curX}px, ${curY}px, 0) scale(${scale})`;

      // 动态掉落阵营色彩尾焰粒子
      if (now - lastSparkTime > 28 && t < 0.94) {
        lastSparkTime = now;
        this.spawnTrailSpark(curX, curY, factionClass);
      }

      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        if (orb.parentNode) orb.parentNode.removeChild(orb);
        if (reticle.parentNode) reticle.parentNode.removeChild(reticle);
        this.detonateImpact(p1.x, p1.y, toEl, factionClass, options.impactText);
        onImpact?.();
      }
    };

    requestAnimationFrame(step);
  }

  private spawnTrailSpark(x: number, y: number, factionClass: "outbound" | "inbound" | "rescue") {
    const spark = document.createElement("div");
    spark.className = `projectile-spark spark-${factionClass}`;
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    this.overlayEl.appendChild(spark);
    setTimeout(() => {
      if (spark.parentNode) spark.parentNode.removeChild(spark);
    }, 280);
  }

  private detonateImpact(
    x: number,
    y: number,
    targetEl: HTMLElement,
    factionClass: "outbound" | "inbound" | "rescue",
    impactText?: string
  ) {
    // 1. 冲击波扩散光环
    const ring = document.createElement("div");
    ring.className = `shockwave-ring ring-${factionClass}`;
    ring.style.left = `${x}px`;
    ring.style.top = `${y}px`;
    this.overlayEl.appendChild(ring);
    setTimeout(() => {
      if (ring.parentNode) ring.parentNode.removeChild(ring);
    }, 450);

    // 2. 弹出直观视觉战果浮字 (若提供)
    if (impactText) {
      this.spawnImpactBadgeAt(x, y, impactText, factionClass === "outbound");
    }

    // 3. 目标棋盘受击震颤 (救援包不震颤)
    if (factionClass !== "rescue") {
      this.shakeBoard(targetEl);
    }
  }

  /**
   * 在指定盘面中心弹出高对比度浮动战果徽标 (如 "512 ➔ 256" 或 "+2 塞块")
   */
  public spawnImpactBadgeOnElement(targetEl: HTMLElement, text: string, isOutbound: boolean) {
    if (!targetEl || !this.overlayEl) return;
    const overlayRect = this.overlayEl.getBoundingClientRect();
    const rect = targetEl.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - overlayRect.left;
    const y = rect.top + rect.height / 2 - overlayRect.top;
    this.spawnImpactBadgeAt(x, y, text, isOutbound);
  }

  private spawnImpactBadgeAt(x: number, y: number, text: string, isOutbound: boolean) {
    const badge = document.createElement("div");
    badge.className = `impact-float-badge ${isOutbound ? "badge-outbound" : "badge-inbound"}`;
    badge.textContent = text;
    badge.style.left = `${x}px`;
    badge.style.top = `${y}px`;
    this.overlayEl.appendChild(badge);
    setTimeout(() => {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    }, 950);
  }

  /**
   * 兼容保留快捷发射入口
   */
  public shootAttackBeam(fromEl: HTMLElement, toEl: HTMLElement) {
    this.launchBallisticProjectile(fromEl, toEl, {
      isOutbound: true,
      label: "+2",
    });
  }

  /**
   * 触发棋盘护盾完美格挡动效与浮标 (当对方狙击的数字在盘面上不存在时触发)
   */
  public playShieldBlockEffect(boardEl: HTMLElement, text: string) {
    if (!boardEl || !this.overlayEl) return;

    boardEl.classList.remove("board-shield-parry");
    void boardEl.offsetWidth;
    boardEl.classList.add("board-shield-parry");
    setTimeout(() => {
      boardEl.classList.remove("board-shield-parry");
    }, 650);

    const overlayRect = this.overlayEl.getBoundingClientRect();
    const rect = boardEl.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - overlayRect.left;
    const y = rect.top + rect.height / 2 - overlayRect.top;

    const badge = document.createElement("div");
    badge.className = "impact-float-badge badge-defended";
    badge.textContent = text;
    badge.style.left = `${x}px`;
    badge.style.top = `${y}px`;
    this.overlayEl.appendChild(badge);
    setTimeout(() => {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    }, 980);
  }

  /**
   * 触发高华丽度 (DMC B ~ SSS) 残影极限闪避动效与 MISS 浮标
   */
  public playDodgeMissEffect(boardEl: HTMLElement, text: string) {
    if (!boardEl || !this.overlayEl) return;

    boardEl.classList.remove("board-dodge-miss");
    void boardEl.offsetWidth;
    boardEl.classList.add("board-dodge-miss");
    setTimeout(() => {
      boardEl.classList.remove("board-dodge-miss");
    }, 620);

    const overlayRect = this.overlayEl.getBoundingClientRect();
    const rect = boardEl.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - overlayRect.left;
    const y = rect.top + rect.height / 2 - overlayRect.top;

    const badge = document.createElement("div");
    badge.className = "impact-float-badge badge-missed";
    badge.textContent = text;
    badge.style.left = `${x}px`;
    badge.style.top = `${y}px`;
    this.overlayEl.appendChild(badge);
    setTimeout(() => {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    }, 980);
  }

  /**
   * 触发棋盘受击震颤
   */
  public shakeBoard(boardEl: HTMLElement) {
    if (!boardEl) return;
    boardEl.classList.remove("board-impact-shake");
    void boardEl.offsetWidth;
    boardEl.classList.add("board-impact-shake");
  }

  /**
   * 漂浮表情弹射
   */
  public spawnFloatingEmote(emoji: string, isFromRival: boolean) {
    if (!this.overlayEl) return;

    const emote = document.createElement("div");
    emote.className = "floating-emote";
    emote.textContent = emoji;

    const screenWidth = window.innerWidth;
    const baseLeft = isFromRival ? screenWidth * 0.65 : screenWidth * 0.25;
    const randomOffset = (Math.random() - 0.5) * 80;

    emote.style.left = `${Math.max(20, Math.min(screenWidth - 60, baseLeft + randomOffset))}px`;
    emote.style.bottom = "20%";

    this.overlayEl.appendChild(emote);

    setTimeout(() => {
      if (emote.parentNode) emote.parentNode.removeChild(emote);
    }, 2000);
  }

  /**
   * 胜利彩带礼花
   */
  public fireVictoryConfetti() {
    try {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      setTimeout(() => {
        confetti({
          particleCount: 60,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
        });
        confetti({
          particleCount: 60,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
        });
      }, 250);
    } catch (_) {}
  }
}

